// ============================================
// 路由配置
// ============================================
// 参考 RuoYi-UI 认证流程重构

import Vue from 'vue'
import VueRouter from 'vue-router'
import store from '../store'
import { getToken } from '../utils/auth'
import { Message } from 'element-ui'

Vue.use(VueRouter)

// ============================================
// 防止连续点击多次路由报错
// ============================================
const originalPush = VueRouter.prototype.push
const originalReplace = VueRouter.prototype.replace

// push
VueRouter.prototype.push = function push(location) {
  return originalPush.call(this, location).catch(err => err)
}

// replace
VueRouter.prototype.replace = function replace(location) {
  return originalReplace.call(this, location).catch(err => err)
}

// ============================================
// 静态路由（无需登录即可访问）
// ============================================
export const constantRoutes = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('../views/login/index.vue'),
    meta: { title: '登录' }
  },
  {
    path: '/404',
    name: '404',
    component: () => import('../views/404.vue'),
    meta: { title: '404' }
  },
  {
    path: '/',
    component: () => import('../layout/Layout.vue'),
    redirect: '/home',
    children: [
      {
        path: 'home',
        name: 'Home',
        component: () => import('../views/home/index.vue'),
        meta: { title: '首页', icon: 'el-icon-s-home', affix: true }
      }
    ]
  }
]


// ============================================
// 创建路由实例
// ============================================
const createRouter = () => new VueRouter({
  mode: 'history',
  scrollBehavior: () => ({ y: 0 }),
  routes: constantRoutes
})

const router = createRouter()

// ============================================
// 重置路由（登出时调用）
// ============================================
export function resetRouter() {
  const newRouter = createRouter()
  router.matcher = newRouter.matcher
}

// ============================================
// 路由守卫（参考 RuoYi-UI）
// ============================================
const whiteList = ['/login', '/404'] // 白名单

router.beforeEach(async (to, from, next) => {
  // 设置页面标题
  document.title = to.meta?.title ? `${to.meta.title} - RBAC管理系统` : 'RBAC管理系统'

  const token = getToken()

  if (token) {
    // ========================================
    // 场景 A：已登录（有 Token）
    // ========================================
    if (to.path === '/login') {
      // A1. 访问登录页 → 重定向到首页
      next({ path: '/' })
    } else {
      // A2. 访问其他页面 → 检查是否已加载用户信息和路由
      // 使用 roles.length === 0 判断
      if (store.getters.roles.length === 0) {
        try {
          // ① 获取用户信息（包含角色和权限）
          await store.dispatch('getInfo')
          
          // ② 生成动态路由
          const accessRoutes = await store.dispatch('generateRoutes')
          
          // ③ 动态添加路由
          accessRoutes.forEach(route => {
            router.addRoute(route)
          })
          
          // ④ 重新进入路由（确保路由生效）
          next({ ...to, replace: true })
        } catch (error) {
          // 获取用户信息失败 → 登出并跳转登录页
          await store.dispatch('logout')
          Message.error(error.message || '加载用户信息失败，请重新登录')
          next(`/login?redirect=${to.path}`)
        }
      } else {
        // 已加载用户信息和路由 → 直接放行
        next()
      }
    }
  } else {
    // ========================================
    // 场景 B：未登录（无 Token）
    // ========================================
    if (whiteList.indexOf(to.path) !== -1) {
      // B1. 访问白名单页面 → 直接放行
      next()
    } else {
      // B2. 访问其他页面 → 跳转登录页
      next(`/login?redirect=${to.path}`)
    }
  }
})

export default router
